import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ASCENDING } from 'constants/sortDirections';
import { Table, sortRows } from 'ui/table';
import { colors, smallShadow, spacing } from 'ui/theme';
import { InputText } from 'ui/inputs';
import { ColumnData, TableHeader } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';
import { pxToRem } from 'utils/convert';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { MetricDefinition } from '../types';

const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${spacing.half};
`;

const StyledInput = styled(InputText)`
  width: ${pxToRem(350)};
`;

const StyledTable = styled(Table)`
  border: ${spacing.single} solid ${colors.grey25};
  ${smallShadow};
`;

const StyledTableHeader = styled(TableHeader)`
  position: sticky;
  top: ${pxToRem(100)};
  ${smallShadow};
  z-index: 10;
`;

export const MetricDefinitionsTable = ({
  columns,
  rows,
}: {
  columns: ColumnData[];
  rows: MetricDefinition[];
}) => {
  const [debouncedFilter, filter, setFilter] = useDebouncedState('', 800);
  const [sortedBy, setSortedBy] = useState('metricName');
  const [sortDirection, setSortDirection] = useState(ASCENDING);

  const sortedRows = useMemo(() => {
    const filteredRows = filter.length
      ? rows.filter((row) => row.metricName.toLowerCase().includes(filter.toLowerCase()))
      : rows;
    return sortRows(filteredRows, sortedBy as keyof MetricDefinition, sortDirection);
  }, [debouncedFilter, rows, sortDirection, sortedBy]);

  return (
    <>
      <ActionsContainer>
        <StyledInput
          placeholder={'Filter metric definitions...'}
          hasError={false}
          id="metricDefinitionFilterInput"
          onChange={setFilter}
          value={filter}
        />
      </ActionsContainer>
      <StyledTable>
        <StyledTableHeader
          columns={columns}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
        />
        {debouncedFilter === filter && <TableRows rows={sortedRows} columns={columns} />}
      </StyledTable>
    </>
  );
};
